import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CheckCircle2, AlertCircle, Sparkles, TrendingUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CASE_STUDIES } from "../data/site";

const SECTIONS = [
  { key: "challenge", title: "Challenge", icon: AlertCircle, tone: "text-amber-300" },
  { key: "solution", title: "Our Solution", icon: Sparkles, tone: "text-[#F5D67B]" },
  { key: "results", title: "Results", icon: TrendingUp, tone: "text-emerald-300" },
];

export default function CaseStudies() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="bg-[#0A0A0A] text-white pt-28 pb-24">
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute -top-24 -left-32 h-[440px] w-[440px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-3">Case Studies</p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] max-w-4xl">
            Real businesses. Real{" "}
            <span className="text-gradient-brand">online growth.</span>
          </h1>
          <p className="mt-6 text-lg text-gray-300 max-w-2xl">
            A behind-the-scenes look at how we help ambitious local businesses become more discoverable, credible and contactable online.
          </p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 space-y-10">
          {CASE_STUDIES.map((c, i) => (
            <article
              key={c.slug}
              data-testid={`case-study-${c.slug}`}
              className="grid lg:grid-cols-12 gap-8 rounded-3xl border border-white/10 bg-[#121212] p-6 lg:p-10 overflow-hidden"
            >
              <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                <div className="rounded-2xl overflow-hidden border border-white/10 h-full">
                  <img src={c.image} alt={c.name} className="w-full h-full min-h-72 object-cover" />
                </div>
              </div>
              <div className="lg:col-span-7">
                <Badge className="bg-white/5 text-[#F5D67B] border border-white/10 mb-3">{c.industry}</Badge>
                <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-6">{c.name}</h2>
                <div className="grid sm:grid-cols-3 gap-5">
                  {SECTIONS.map(({ key, title, icon: Icon, tone }) => (
                    <div key={key} data-testid={`case-${c.slug}-${key}`}>
                      <div className="flex items-center gap-2 mb-3">
                        <Icon className={`h-4 w-4 ${tone}`} />
                        <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-gray-200">{title}</h3>
                      </div>
                      <ul className="space-y-2">
                        {c[key].map((p, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-gray-300">
                            <CheckCircle2 className="h-3.5 w-3.5 text-[#D4AF37] mt-1 shrink-0" />
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="mt-8">
                  <Link to="/contact" data-testid={`case-cta-${c.slug}`}>
                    <Button className="bg-gradient-brand text-[#0A0A0A] rounded-full h-11 px-6 font-semibold">
                      Discuss Your Project <ArrowUpRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mt-16">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="rounded-3xl border border-white/10 bg-[#121212] p-8 md:p-12 text-center">
            <p className="text-gray-300 leading-relaxed max-w-2xl mx-auto">
              More client success stories and testimonials will be added as new projects are completed.
            </p>
            <div className="mt-6">
              <Link to="/contact" data-testid="case-studies-cta-button">
                <Button className="bg-gradient-brand text-[#0A0A0A] rounded-full h-12 px-7 font-semibold">
                  Be Our Next Success Story <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
